<template>
  <CustomDrawer
    v-model:modelValue="internalDrawerVisible"
    direction="right"
    width="500px"
    contentCSS="custom-content-css"
    :title="
      isEditing ? 'Edit Value Prop Template' : 'Create Value Prop Template'
    "
  >
    <div class="space-y-6 text-[13px] secondary_color proxima_nova font-normal">
      <div class="form_col">
        <span>Template Name*</span>
        <a-input
          ref="templateNameInputRef"
          allow-clear
          v-model:value="templateName"
          class="input_medium"
          placeholder="Enter value prop template name"
        />
      </div>

      <div class="form_col">
        <span>Company Website</span>
        <div class="flex flex-row items-start gap-2">
          <div class="flex-1 relative">
            <a-input
              v-model:value="formState.company_website"
              allow-clear
              class="input_medium"
              :status="websiteError ? 'error' : ''"
              placeholder="https://www.website.com"
              @input="validateWebsiteInput"
              :disabled="isLoadingCompanyInsights"
            />
            <div v-if="websiteError" class="text-red-500 text-xs mt-1">
              {{ websiteError }}
            </div>
          </div>
          <a-button
            :loading="isLoadingCompanyInsights"
            class="primary_medium"
            @click="handleFetchCompanyInsights"
            :disabled="
              isLoadingCompanyInsights || !formState.company_website.trim()
            "
          >
            <ThemeImage
              :icon="imageNames.AGI_PURPLE"
              alt="note_icon"
              class="size-4"
            />
            Generate Template
          </a-button>
        </div>
      </div>

      <div v-if="isLoadingCompanyInsights" class="flex flex-col gap-x-2.5">
        <div class="flex flex-row items-center">
          <ThemeImage
            class="size-4 mr-1"
            :icon="imageNames.AGI_PURPLE"
            alt="note_icon"
          />
          Generating details...
        </div>
        <div class="loading_placeholder">
          <div class="loading_line custom_skeleton"></div>
          <div class="loading_line short custom_skeleton"></div>
        </div>
      </div>

      <div v-if="!isLoadingCompanyInsights" class="space-y-6">
        <div class="form_col">
          <span>Company Name*</span>
          <a-input
            v-model:value="formState.company_name"
            allow-clear
            class="input_medium"
            placeholder="Enter company name"
            :disabled="isLoadingCompanyInsights"
          />
        </div>

        <div class="form_col">
          <span>What are you selling*</span>
          <a-textarea
            v-model:value="formState.product_description"
            class="input_medium"
            :rows="4"
            placeholder="SuperAGI will find prospects that suit your criteria. Details about your ideal customer."
            :disabled="isLoadingCompanyInsights"
          />
        </div>

        <div class="form_col">
          <span>Pain Points</span>
          <div
            v-for="(point, index) in formState.pain_points"
            :key="index"
            class="mb-2 flex items-center gap-2"
          >
            <a-input
              v-model:value="formState.pain_points[index]"
              placeholder="Pain point"
              class="input_medium flex-grow"
              :disabled="isLoadingCompanyInsights"
            />
            <a-button
              class="secondary_medium"
              @click="removePainPoint(index)"
              :disabled="isLoadingCompanyInsights"
            >
              <ThemeImage
                :icon="imageNames.CLOSE_GREY"
                alt="close_icon"
                class="cursor-pointer"
              />
            </a-button>
          </div>
          <div class="flex items-center">
            <a-input
              v-model:value="newPainPoint"
              placeholder="Pain point example statement"
              class="input_medium"
              @pressEnter="addPainPoint"
              @blur="addPainPoint"
              :disabled="isLoadingCompanyInsights"
            />
          </div>
          <div class="flex flex-row gap-2 justify-start">
            <a-button
              class="mt-2 secondary_medium"
              @click="addPainPoint"
              :disabled="isLoadingCompanyInsights"
              >+ Add</a-button
            >
          </div>
        </div>

        <div class="form_col">
          <span>Value Propositions</span>
          <div
            v-for="(prop, index) in formState.value_propositions"
            :key="index"
            class="mb-2 flex items-center gap-2"
          >
            <a-input
              v-model:value="formState.value_propositions[index]"
              placeholder="Value proposition"
              class="input_medium flex-grow"
              :disabled="isLoadingCompanyInsights"
            />
            <a-button
              class="secondary_medium"
              @click="removeValueProposition(index)"
              :disabled="isLoadingCompanyInsights"
            >
              <ThemeImage
                :icon="imageNames.CLOSE_GREY"
                alt="close_icon"
                class="cursor-pointer"
              />
            </a-button>
          </div>
          <div class="flex items-center">
            <a-input
              v-model:value="newValueProposition"
              placeholder="Value proposition example statement"
              class="input_medium"
              @pressEnter="addValueProposition"
              @blur="addValueProposition"
              :disabled="isLoadingCompanyInsights"
            />
          </div>
          <div class="flex flex-row gap-2 justify-start">
            <a-button
              class="mt-2 secondary_medium"
              @click="addValueProposition"
              :disabled="isLoadingCompanyInsights"
              >+ Add</a-button
            >
          </div>
        </div>

        <div class="form_col">
          <span>Proof Points</span>
          <div
            v-for="(point, index) in formState.proof_points"
            :key="index"
            class="mb-2 flex items-center gap-2"
          >
            <a-input
              v-model:value="formState.proof_points[index]"
              placeholder="Proof point"
              class="input_medium flex-grow"
              :disabled="isLoadingCompanyInsights"
            />
            <a-button
              class="secondary_medium"
              @click="removeProofPoint(index)"
              :disabled="isLoadingCompanyInsights"
            >
              <ThemeImage
                :icon="imageNames.CLOSE_GREY"
                alt="close_icon"
                class="cursor-pointer"
              />
            </a-button>
          </div>
          <div class="flex items-center">
            <a-input
              v-model:value="newProofPoint"
              placeholder="Proof point example statement"
              @pressEnter="addProofPoint"
              class="input_medium"
              @blur="addProofPoint"
              :disabled="isLoadingCompanyInsights"
            />
          </div>
          <div class="flex flex-row gap-2 justify-start">
            <a-button
              class="mt-2 secondary_medium"
              @click="addProofPoint"
              :disabled="isLoadingCompanyInsights"
              >+ Add</a-button
            >
          </div>
        </div>

        <div class="form_col">
          <span>Personality</span>
          <a-select
            v-model:value="formState.personality"
            :disabled="isLoadingCompanyInsights"
          >
            <template #suffixIcon
              ><ThemeImage
                class="size-4"
                :icon="imageNames.BOTTOM_ARROW"
                alt="bottom_white_arrow"
            /></template>
            <a-select-option
              v-for="(option, index) in personalityOptions"
              :label="option.value"
              :key="index"
              :value="option.value"
            >
              {{ option.label }}
            </a-select-option>
          </a-select>
        </div>

        <div class="form_col">
          <span>Outreach Language</span>
          <a-select v-model:value="formState.outreach_language" disabled>
            <template #suffixIcon
              ><ThemeImage
                class="size-4"
                :icon="imageNames.BOTTOM_ARROW"
                alt="bottom_white_arrow"
            /></template>
            <a-select-option
              v-for="(option, index) in languageOptions"
              :label="option.value"
              :key="index"
              :value="option.value"
            >
              {{ option.label }}
            </a-select-option>
          </a-select>
        </div>

        <div class="form_col">
          <span class="flex"
            >Train {{ featureNames.COLD_OUTREACH }} for Emails
            <a-tooltip placement="top">
              <template v-slot:title>
                <span
                  >Train {{ featureNames.COLD_OUTREACH }} by providing sample
                  emails
                </span>
              </template>
              <InfoCircleOutlined class="ml-1" />
            </a-tooltip>
          </span>
          <a-textarea
            v-model:value="formState.example_emails"
            class="input_medium"
            :rows="4"
            :placeholder="`Train ${featureNames.COLD_OUTREACH} by providing sample emails`"
            @blur="addExampleEmails"
            :disabled="isLoadingCompanyInsights"
          />
        </div>

        <div class="form_col">
          <span class="flex"
            >Give Instructions to
            {{
              WorkspaceService.isAiHr()
                ? "AI Recruiter"
                : featureNames.COLD_OUTREACH
            }}
            <a-tooltip placement="top">
              <template v-slot:title>
                <span>Enter instructions here</span>
              </template>
              <InfoCircleOutlined class="ml-1" />
            </a-tooltip>
          </span>
          <div
            v-for="(point, index) in formState.instructions"
            :key="`instructions_${index}`"
            class="mb-2 flex items-center gap-2"
          >
            <a-input
              v-model:value="formState.instructions[index]"
              placeholder="Instructions"
              class="input_medium flex-grow"
              :disabled="isLoadingCompanyInsights"
            />
            <a-button
              class="secondary_medium"
              @click="removeInstruction(index)"
              :disabled="isLoadingCompanyInsights"
            >
              <ThemeImage
                :icon="imageNames.CLOSE_GREY"
                alt="close_icon"
                class="cursor-pointer"
              />
            </a-button>
          </div>

          <div class="flex items-center">
            <a-input
              v-model:value="newInstruction"
              placeholder="Enter instructions here"
              @pressEnter="addInstruction"
              class="input_medium"
              @blur="addInstruction"
              :disabled="isLoadingCompanyInsights"
            />
          </div>

          <div class="flex flex-row gap-2 justify-start">
            <a-button
              class="mt-2 secondary_medium"
              @click="addInstruction"
              :disabled="isLoadingCompanyInsights"
              >+ Add
            </a-button>
          </div>
        </div>

        <div>
          <a-switch
            v-model:checked="hasCallToAction"
            :disabled="isLoadingCompanyInsights"
          />
          <span
            class="text-[13px] secondary_color proxima_nova font-normal ml-2"
            >Call to Action</span
          >
        </div>

        <div v-if="hasCallToAction">
          <div class="form_col mb-4">
            <span>CTA Button Text</span>
            <a-input
              v-model:value="formState.call_to_action"
              class="input_medium"
              placeholder="Call Us Today"
              allow-clear
              :disabled="isLoadingCompanyInsights"
            />
          </div>

          <div class="grid grid-cols-2 gap-2 w-full">
            <div class="form_col relative">
              <span>CTA Link Type</span>
              <a-select
                v-model:value="formState.url_type"
                class="w-full"
                placeholder="Custom"
                @change="handleURLTypeChange"
                :disabled="isLoadingCompanyInsights"
              >
                <template #suffixIcon>
                  <ThemeImage
                    :icon="imageNames.BOTTOM_ARROW"
                    alt="bottom_white_arrow"
                    class="size-4"
                  />
                </template>
                <a-select-option value="Custom">Custom</a-select-option>
                <a-select-option value="Meeting Agent Link"
                  >Meeting Agent Link</a-select-option
                >
              </a-select>
            </div>

            <div class="form_col relative">
              <span>CTA Link*</span>
              <template
                v-if="formState.url_type === 'Custom' || !formState.url_type"
              >
                <a-input
                  v-model:value="formState.call_to_action_link"
                  class="w-full"
                  placeholder="www.website.com"
                  allow-clear
                  :disabled="isLoadingCompanyInsights"
                />
              </template>

              <template v-else>
                <a-select
                  v-model:value="formState.call_to_action_link"
                  class="w-full"
                  :placeholder="selectedAgentName || 'Select a Meeting Agent'"
                  :allowClear="false"
                  :disabled="isLoadingCompanyInsights"
                >
                  <template
                    v-for="(agent, index) in [...agentDetails.agents, 'manage']"
                    :key="index"
                  >
                    <a-select-option
                      v-if="agent !== 'manage'"
                      :value="agent.public_link"
                      class="py-2"
                    >
                      <div>
                        <div class="font-medium">{{ agent.name }}</div>
                        <div
                          class="secondary_color font-light text-xs flex items-center"
                        >
                          {{
                            agent.type === "round_robin"
                              ? "Round Robin"
                              : "One-on-one"
                          }}
                        </div>
                        <div
                          style="background: var(--white-opacity-10)"
                          class="inline-block text_color min-w-[50px] whitespace-nowrap px-2 py-[2px] rounded-2xl text-[11px] font-medium"
                        >
                          {{ agent.duration }}min
                        </div>
                      </div>
                    </a-select-option>

                    <a-select-option
                      v-else
                      class="py-2"
                      @mousedown.prevent="goToManageMeetingAgentPage"
                    >
                      <div class="flex items-center">
                        <ThemeImage
                          class="size-4 mr-2"
                          :icon="imageNames.SETTINGS_GREY"
                          alt="manage_icon"
                        />
                        <span>Manage Meeting Agents</span>
                      </div>
                    </a-select-option>
                  </template>
                </a-select>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div ref="scrollContainer" v-if="initialFetchCompleted"></div>
    <template #footer>
      <div class="flex flex-row gap-2 justify-end">
        <a-button class="secondary_medium" @click="onCancel">Cancel</a-button>
        <a-button class="primary_medium" @click="onFinish"
          >{{ isEditing ? "Update Template" : "Create Template" }}
        </a-button>
      </div>
    </template>
  </CustomDrawer>
</template>

<script setup>
import { ref, reactive, onMounted, watch, nextTick, computed } from "vue";
import { message } from "ant-design-vue";
import OutreachService from "@/src/api/OutreachService.js";
import DashboardService from "../../api/DashboardService";
import CustomDrawer from "@/src/components/CustomDrawer.vue";
import { useRouter } from "vue-router";
import WorkspaceService from "../../api/WorkspaceService.js";
import { InfoCircleOutlined } from "@ant-design/icons-vue";
import { isDomainValid } from "@/src/utils/utils.js";
import { imageNames } from "@/src/constants/imageConstants.js";
import { featureNames } from "@/src/constants/globalConstants";

const props = defineProps({
  templateId: { type: Number, default: null },
  isEditing: { type: Boolean, default: null },
  drawerVisible: { type: Boolean, required: true },
});
const router = useRouter();
const emit = defineEmits(["cancel", "pipeline-saved", "update:modelValue"]);
const internalDrawerVisible = ref(props.drawerVisible);
const templateNameInputRef = ref(null);
const hasCallToAction = ref(true);
const templateName = ref("");
const initialFetchCompleted = ref(false);
const scrollContainer = ref(null);
const isLoadingCompanyInsights = ref(false);
const websiteError = ref("");
const personalityOptions = ref([
  { value: "awestruck", label: "😲 Awestruck" },
  { value: "bold", label: "💪 Bold" },
  { value: "compassionate", label: "😊 Compassionate" },
  { value: "convincing", label: "🙂 Convincing" },
  { value: "enthusiastic", label: "😃 Enthusiastic" },
  { value: "formal", label: "👔 Formal" },
  { value: "friendly", label: "😊 Friendly" },
  { value: "joyful", label: "🎉 Joyful" },
  { value: "professional", label: "💼 Professional" },
  { value: "relaxed", label: "⛱️ Relaxed" },
]);

const languageOptions = ref([
  { value: "en-US", label: "🇺🇸 American English" },
  { value: "en-GB", label: "🇬🇧 British English" },
  { value: "es", label: "🇪🇸 Spanish" },
  { value: "nl", label: "🇳🇱 Dutch" },
  { value: "fr", label: "🇫🇷 French" },
  { value: "de", label: "🇩🇪 German" },
]);

const agentDetails = ref({
  success: false,
  calendar_integrated: false,
  agents: [],
});

const formState = reactive({
  company_name: "",
  company_website: "",
  product_description: "",
  pain_points: [],
  value_propositions: [],
  proof_points: [],
  personality: "friendly",
  outreach_language: "en-US",
  example_emails: "",
  instructions: [],
  call_to_action: "",
  call_to_action_link: "",
  has_call_to_action: true,
  url_type: "Meeting Agent Link",
});

const newPainPoint = ref("");
const newValueProposition = ref("");
const newProofPoint = ref("");
const newInstruction = ref("");
const newExampleEmail = ref("");
const abortController = ref(null);

const addPainPoint = () => {
  if (newPainPoint.value.trim()) {
    formState.pain_points.push(newPainPoint.value.trim());
    newPainPoint.value = "";
  }
};

const removePainPoint = (index) => {
  formState.pain_points.splice(index, 1);
};

const addValueProposition = () => {
  if (newValueProposition.value.trim()) {
    formState.value_propositions.push(newValueProposition.value.trim());
    newValueProposition.value = "";
  }
};

const removeValueProposition = (index) => {
  formState.value_propositions.splice(index, 1);
};

const addProofPoint = () => {
  if (newProofPoint.value.trim()) {
    formState.proof_points.push(newProofPoint.value.trim());
    newProofPoint.value = "";
  }
};

const removeProofPoint = (index) => {
  formState.proof_points.splice(index, 1);
};

const addExampleEmails = () => {
  const email = newExampleEmail.value.trim();
  const wordCount = email.split(/\s+/).length;

  if (wordCount > 500) {
    message.error("Word limit exceeded. Maximum allowed is 500 words.");
    return;
  }

  if (email) {
    formState.example_emails = email;
    newExampleEmail.value = "";
  }

  saveForm();
};

const addInstruction = () => {
  const instruction = newInstruction.value.trim();
  const wordCount = instruction.split(/\s+/).length;

  if (wordCount > 120) {
    message.error("Word limit exceeded. Maximum allowed is 120 words.");
    return;
  }

  if (newInstruction.value.trim()) {
    formState.instructions.push(instruction);
    newInstruction.value = "";
  }

  saveForm();
};

const removeInstruction = (index) => {
  formState.instructions.splice(index, 1);
  saveForm();
};

const fetchTemplateDetails = async () => {
  if (!props.templateId) return;
  try {
    const response = await OutreachService.fetchCampaignTemplateDetails(
      props.templateId,
    );
    if (response.success) {
      templateName.value = response.template_name;
      let offer = response.offer;
      Object.assign(formState, {
        company_name: offer.company_name || "",
        company_website: offer.company_website || "",
        product_description: offer.product_description || "",
        pain_points: Array.isArray(offer.pain_points) ? offer.pain_points : [],
        value_propositions: Array.isArray(response.offer.value_propositions)
          ? offer.value_propositions
          : [],
        proof_points: Array.isArray(offer.proof_points)
          ? offer.proof_points
          : [],
        personality: offer.personality || "friendly",
        outreach_language: offer.outreach_language || "en-US",
        instructions: Array.isArray(offer.instructions)
          ? offer.instructions
          : [],
        example_emails: offer.example_emails || "",
        call_to_action: offer.call_to_action || "",
        call_to_action_link: offer.call_to_action_link || "",
        url_type: offer.url_type || "",
        has_call_to_action: offer.has_call_to_action ?? true,
      });

      hasCallToAction.value =
        offer.has_call_to_action ??
        (formState.call_to_action !== "" ||
          formState.call_to_action_link !== "");
    } else {
      message.error(response.message);
    }
  } catch (error) {
    console.error("Error fetching template details:", error);
    message.error("An error occurred while fetching template details");
  } finally {
    setTimeout(() => {
      initialFetchCompleted.value = true;
    }, 1000);
  }
};

const fetchMeetingAgents = async () => {
  try {
    const params = {
      page_size: -1,
      page: 1,
      search_query: "",
    };
    const response = await DashboardService.fetchMeetingAgents(params);
    if (
      response?.success &&
      response?.calendar_integrated &&
      response?.agents &&
      response?.agents?.length > 0
    ) {
      agentDetails.value = response;
    } else {
      agentDetails.value = {
        success: false,
        calendar_integrated: false,
        agents: [],
      };
    }
  } catch (error) {
    agentDetails.value = {
      success: false,
      calendar_integrated: false,
      agents: [],
    };
  }
};

watch(
  () => internalDrawerVisible.value,
  (newVal) => {
    if (!newVal) {
      refreshData();
      emit("update:modelValue", false);
      abortOngoingRequests();
    } else {
      nextTick(() => {
        if (templateNameInputRef.value) {
          templateNameInputRef.value?.focus();
        }
      });
    }
  },
);

const onCancel = () => {
  abortOngoingRequests();
  emit("update:modelValue", false);
};

const refreshData = async () => {
  templateName.value = "";
  Object.assign(formState, {
    company_name: "",
    company_website: "",
    product_description: "",
    pain_points: [],
    value_propositions: [],
    proof_points: [],
    personality: "friendly",
    outreach_language: "en-US",
    example_emails: "",
    instructions: [],
    call_to_action: "",
    call_to_action_link: "",
    url_type: "Meeting Agent Link",
    has_call_to_action: true,
  });
  hasCallToAction.value = true;
};

watch(
  () => props.drawerVisible,
  (newVal) => {
    internalDrawerVisible.value = newVal;
    if (newVal) {
      if (props.isEditing) {
        fetchTemplateDetails();
      } else {
        hasCallToAction.value = true;
        formState.has_call_to_action = true;
        formState.url_type = "Meeting Agent Link";
      }
    }
  },
);

watch(
  () => hasCallToAction.value,
  (newValue) => {
    formState.has_call_to_action = newValue;

    if (newValue && initialFetchCompleted.value) {
      nextTick(() => {
        const target = scrollContainer.value;
        if (target) {
          target.scrollIntoView({ behavior: "smooth", block: "end" });
        }
      });
    }
  },
);

const validateWebsiteInput = () => {
  if (websiteError.value) {
    websiteError.value = "";
  }
};

const handleFetchCompanyInsights = () => {
  if (!formState.company_website.trim()) {
    message.error("Please enter a company website");
    return;
  }

  let urlString = formState.company_website.trim();
  let domain = urlString;

  if (!domain.startsWith("http://") && !domain.startsWith("https://")) {
    domain = "https://" + domain;
  }

  try {
    const url = new URL(domain);
    let hostname = url.hostname;

    if (hostname.startsWith("www.")) {
      hostname = hostname.substring(4);
    }

    if (isDomainValid(hostname)) {
      websiteError.value = "";
      getCompanyInsights(formState.company_website);
    } else {
      websiteError.value = "Please enter a valid website URL";
    }
  } catch (e) {
    websiteError.value = "Please enter a valid website URL";
  }
};

const onFinish = async () => {
  if (templateName.value.trim() === "") {
    message.error("Please enter valid template name");
    return;
  }

  if (formState.company_name.trim() === "") {
    message.error("Please enter valid company name");
    return;
  }

  if (formState.product_description.trim() === "") {
    message.error("Please enter what are you selling");
    return;
  }

  if (formState.has_call_to_action && !formState.call_to_action_link) {
    message.error(
      "Please enter a CTA link or disable the toggle for Call to Action",
    );
    return;
  }

  try {
    let response;
    response = await OutreachService.saveCampaignTemplateDetails(
      props.templateId,
      formState,
      templateName.value,
    );

    if (response.success) {
      message.success(
        `Template ${props.isEditing ? "updated" : "created"} successfully`,
      );
      emit("template-saved", true);
      emit("update:modelValue", false);
    } else {
      message.error(response.message);
    }
  } catch (error) {
    console.error(
      `Error ${props.isEditing ? "updat" : "creat"}ing template:`,
      error,
    );
    message.error(
      `An error occurred while ${props.isEditing ? "updat" : "creat"}ing the template`,
    );
  }
};

const handleURLTypeChange = (value) => {
  formState.url_type = value;
  if (formState.url_type === "Custom") {
    formState.call_to_action_link = "";
  } else if (formState.url_type === "Meeting Agent Link") {
    if (
      !agentDetails.value.agents.some(
        (agent) => agent.public_link === formState.call_to_action_link,
      )
    ) {
      formState.call_to_action_link = "";
    }
  }
};

const getCompanyInsights = async (url) => {
  if (!url || !url.trim()) return;

  try {
    isLoadingCompanyInsights.value = true;

    abortController.value = new AbortController();
    const signal = abortController.value.signal;

    try {
      const startResponse = await DashboardService.startCompanyInsightsResearch(
        url,
        { signal },
      );
      if (!startResponse.success) {
        isLoadingCompanyInsights.value = false;
        return;
      }
    } catch (error) {
      if (error.name === "AbortError") {
        return;
      }
      message.error(
        `Error starting company insights research: ${error.message || error}`,
      );
      isLoadingCompanyInsights.value = false;
      return;
    }

    let attempts = 0;
    const maxAttempts = 20;
    const pollInterval = 15000;

    const pollForResults = async () => {
      try {
        if (signal.aborted) {
          return true;
        }

        const response = await DashboardService.getCompanyInsightsResearch(
          url,
          { signal },
        );

        if (!response) {
          message.error("Failed to fetch company insights - no response", 5);
          return true;
        }

        if (response.research_status === "DONE" && response.insights) {
          populateFormWithInsights(response.insights);
          return true;
        } else if (attempts >= maxAttempts) {
          message.error(
            "Unable to get the details. Please check the company website and try again.",
          );
          return true;
        } else {
          attempts++;
          return false;
        }
      } catch (error) {
        if (error.name === "AbortError") {
          return true;
        }
        message.error(
          `Error fetching company insights: ${error.message || "Please try again"}`,
        );
        return true;
      }
    };

    const poll = async () => {
      if (signal.aborted) {
        isLoadingCompanyInsights.value = false;
        return;
      }

      const shouldStop = await pollForResults();
      if (!shouldStop) {
        setTimeout(poll, pollInterval);
      } else {
        isLoadingCompanyInsights.value = false;
      }
    };

    poll();
  } catch (error) {
    if (error.name === "AbortError") {
      return;
    }
    isLoadingCompanyInsights.value = false;
    message.error(
      `Error fetching company insights: ${error.message || "Please try again"}`,
    );
  }
};

const populateFormWithInsights = (insights) => {
  if (!insights) return;

  if (insights.company_name) {
    formState.company_name = insights.company_name;
  }

  if (insights.products) {
    formState.product_description = insights.products;
  }

  if (Array.isArray(insights.pain_points) && insights.pain_points.length > 0) {
    formState.pain_points = [...insights.pain_points];
  }

  if (Array.isArray(insights.value_props) && insights.value_props.length > 0) {
    formState.value_propositions = [...insights.value_props];
  }

  if (
    Array.isArray(insights.proof_points) &&
    insights.proof_points.length > 0
  ) {
    formState.proof_points = [...insights.proof_points];
  }
};

const selectedAgentName = computed(() => {
  if (!formState.call_to_action_link) return "";
  if (!agentDetails.value?.agents?.length) return "";
  if (formState.call_to_action_link) {
    const agent = agentDetails.value.agents.find(
      (agent) => agent.public_link === formState.call_to_action_link,
    );
    return agent
      ? `${agent.name} (${agent.type === "round_robin" ? "Round Robin" : "One-on-one"} · ${agent.duration}min)`
      : formState.call_to_action_link;
  }
  return "";
});

const goToManageMeetingAgentPage = () => {
  router.push({ path: "/meeting_agent" });
};

const abortOngoingRequests = () => {
  if (abortController.value) {
    abortController.value.abort();
    abortController.value = null;
  }
  isLoadingCompanyInsights.value = false;
};

onMounted(async () => {
  await fetchTemplateDetails();
  await fetchMeetingAgents();
});
</script>

<style scoped>
.loading_placeholder {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
  margin-top: 10px;
}
.loading_line {
  height: 16px;
  background-color: var(--white-opacity-6);
  border-radius: 4px;
}
.loading_line.short {
  width: 65%;
}
</style>
